import { useRef, useState, useCallback, useMemo, useEffect } from "react";

interface WaterholeDot {
  baseCx: number;
  baseCy: number;
  r: number;
  opacity: number;
  delay: number;
  ring: number;
}

/**
 * Concentric dot rings — inspired by Aboriginal "meeting place" or "waterhole" symbols.
 * Highly detailed with interactive water-ripple effect on mouse hover.
 */
const DotWaterhole = ({ className = "", size = 300 }: { className?: string; size?: number }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hasEntered, setHasEntered] = useState(false);
  const mousePosRef = useRef<{ x: number; y: number } | null>(null);
  const rafRef = useRef<number>(0);
  const circlesRef = useRef<SVGCircleElement[]>([]);
  const offsetsRef = useRef<Float32Array | null>(null);
  const center = size / 2;

  const dots = useMemo(() => {
    const result: WaterholeDot[] = [];

    // Central cluster
    result.push({ baseCx: center, baseCy: center, r: 3.5, opacity: 0.55, delay: 0, ring: 0 });
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      result.push({ baseCx: center + Math.cos(a) * 5, baseCy: center + Math.sin(a) * 5, r: 2, opacity: 0.45, delay: 0.02, ring: 0 });
    }

    const totalRings = size > 600 ? 10 : 14;
    for (let ring = 1; ring <= totalRings; ring++) {
      const radius = ring * (size / (totalRings * 2.3));
      const count = Math.floor(ring * 6 + ring * 1.5);
      const angleOffset = ring * 0.25;
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + angleOffset;
        const jitter = Math.sin(i * 7.3 + ring * 2.1) * 1.2;
        const px = center + Math.cos(angle) * (radius + jitter);
        const py = center + Math.sin(angle) * (radius + jitter);
        const baseSize = ring <= 3 ? 2.2 : ring <= 7 ? 1.8 : ring <= 11 ? 1.4 : 1.1;
        const sizeVar = Math.sin(i * 2.3 + ring * 1.7) * 0.4;
        result.push({
          baseCx: px, baseCy: py,
          r: baseSize + sizeVar,
          opacity: 0.12 + (1 - ring / (totalRings + 1)) * 0.35 + Math.sin(i * 0.7) * 0.04,
          delay: ring * 0.04 + i * 0.002,
          ring,
        });
      }
    }
    return result;
  }, [size, center]);

  // Use IntersectionObserver instead of framer-motion useInView
  useEffect(() => {
    const el = svgRef.current;
    if (!el || hasEntered) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setHasEntered(true); observer.disconnect(); } },
      { rootMargin: "400px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasEntered]);

  // Staggered entrance: set opacity on each circle after its delay
  useEffect(() => {
    if (!hasEntered) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    dots.forEach((d, i) => {
      const el = circlesRef.current[i];
      if (!el) return;
      const t = setTimeout(() => {
        el.style.opacity = String(d.opacity);
        el.style.transform = 'scale(1)';
      }, d.delay * 1000);
      timers.push(t);
    });
    return () => timers.forEach(clearTimeout);
  }, [hasEntered, dots]);

  // Init offsets
  useEffect(() => {
    offsetsRef.current = new Float32Array(dots.length * 2);
  }, [dots.length]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    mousePosRef.current = {
      x: ((e.clientX - rect.left) / rect.width) * size,
      y: ((e.clientY - rect.top) / rect.height) * size,
    };
  }, [size]);

  const handleMouseLeave = useCallback(() => {
    mousePosRef.current = null;
    isHoveringRef.current = false;
  }, []);

  const isHoveringRef = useRef(false);

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true;
    const loop = () => {
      const offsets = offsetsRef.current;
      if (!offsets) { if (isHoveringRef.current) rafRef.current = requestAnimationFrame(loop); return; }
      const mouse = mousePosRef.current;
      const influenceRadius = size * 0.4;
      const damping = 0.92;
      const lerp = 0.18;
      const strength = 22;
      let anyMoving = false;
      for (let i = 0; i < dots.length; i++) {
        const idx = i * 2;
        let dx = offsets[idx];
        let dy = offsets[idx + 1];
        const dot = dots[i];
        if (mouse) {
          const ddx = dot.baseCx - mouse.x;
          const ddy = dot.baseCy - mouse.y;
          const dist = Math.sqrt(ddx * ddx + ddy * ddy);
          if (dist < influenceRadius && dist > 0) {
            const factor = (1 - dist / influenceRadius);
            const s = factor * strength;
            const angle = Math.atan2(ddy, ddx);
            const swirl = angle + Math.PI * 0.35 * factor;
            dx += (Math.cos(swirl) * s - dx) * lerp;
            dy += (Math.sin(swirl) * s - dy) * lerp;
          } else { dx *= damping; dy *= damping; }
        } else { dx *= damping; dy *= damping; }
        offsets[idx] = dx;
        offsets[idx + 1] = dy;
        if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) anyMoving = true;
        const el = circlesRef.current[i];
        if (el) {
          el.setAttribute('cx', String(dot.baseCx + dx));
          el.setAttribute('cy', String(dot.baseCy + dy));
        }
      }
      if (anyMoving || isHoveringRef.current) rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  }, [dots, size]);

  return (
    <svg
      ref={svgRef}
      className={`${className} cursor-pointer`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      aria-hidden="true"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {dots.map((d, i) => (
        <circle
          key={i}
          ref={(el) => { if (el) circlesRef.current[i] = el; }}
          cx={d.baseCx}
          cy={d.baseCy}
          r={d.r}
          fill="currentColor"
          style={{
            opacity: 0,
            transform: 'scale(0)',
            transformOrigin: `${d.baseCx}px ${d.baseCy}px`,
            transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
          }}
        />
      ))}
    </svg>
  );
};

/**
 * Flowing dot trail
 */
const DotRiverTrail = ({ className = "", width = 600, trails = 3 }: { className?: string; width?: number; trails?: number }) => {
  const height = trails * 30 + 40;

  return (
    <svg className={className} width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden="true">
      {Array.from({ length: trails }).map((_, t) => {
        const baseY = 20 + t * 30;
        const dots = Array.from({ length: 40 }).map((_, i) => {
          const x = (i / 39) * width;
          const wave = Math.sin((i / 39) * Math.PI * 3 + t * 1.2) * 10;
          const r = 1.5 + Math.sin(i * 0.4 + t) * 0.7;
          return { cx: x, cy: baseY + wave, r };
        });
        return dots.map((d, i) => (
          <circle
            key={`${t}-${i}`}
            cx={d.cx}
            cy={d.cy}
            r={d.r}
            fill="currentColor"
            opacity={0.2 + Math.sin(i * 0.3) * 0.1}
          />
        ));
      })}
    </svg>
  );
};

/**
 * U-Shape (meeting place)
 */
const DotUShape = ({ className = "", size = 100 }: { className?: string; size?: number }) => {
  const dots: { cx: number; cy: number; r: number }[] = [];
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.32;

  for (let i = 0; i < 5; i++) {
    dots.push({ cx: cx - radius, cy: cy - radius + i * (radius * 0.45), r: 2 });
  }
  for (let i = 0; i <= 12; i++) {
    const angle = Math.PI + (i / 12) * Math.PI;
    dots.push({
      cx: cx + Math.cos(angle) * radius,
      cy: cy + radius * 0.8 + Math.sin(angle) * radius * 0.6,
      r: 2,
    });
  }
  for (let i = 0; i < 5; i++) {
    dots.push({ cx: cx + radius, cy: cy - radius + i * (radius * 0.45), r: 2 });
  }

  return (
    <svg className={className} width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" aria-hidden="true">
      {dots.map((d, i) => (
        <circle key={i} cx={d.cx} cy={d.cy} r={d.r} fill="currentColor" opacity="0.35" />
      ))}
    </svg>
  );
};

/**
 * Dot diamond / cross pattern
 */
const DotStarburst = ({ className = "", size = 200 }: { className?: string; size?: number }) => {
  const center = size / 2;
  const dots: { cx: number; cy: number; r: number; opacity: number }[] = [];

  for (let arm = 0; arm < 8; arm++) {
    const angle = (arm / 8) * Math.PI * 2;
    for (let d = 1; d <= 7; d++) {
      const dist = d * (size / 18);
      dots.push({
        cx: center + Math.cos(angle) * dist,
        cy: center + Math.sin(angle) * dist,
        r: 2.5 - d * 0.2,
        opacity: 0.35 - d * 0.03,
      });
    }
  }

  for (let i = 0; i < 12; i++) {
    const angle = (i / 12) * Math.PI * 2;
    dots.push({
      cx: center + Math.cos(angle) * (size / 10),
      cy: center + Math.sin(angle) * (size / 10),
      r: 1.5,
      opacity: 0.3,
    });
  }

  dots.push({ cx: center, cy: center, r: 3, opacity: 0.45 });

  return (
    <svg className={className} width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" aria-hidden="true">
      {dots.map((d, i) => (
        <circle key={i} cx={d.cx} cy={d.cy} r={d.r} fill="currentColor" opacity={d.opacity} />
      ))}
    </svg>
  );
};

/**
 * Journey line
 */
const DotJourneyPath = ({ className = "" }: { className?: string }) => {
  const points: { cx: number; cy: number; r: number }[] = [];
  for (let i = 0; i <= 50; i++) {
    const t = i / 50;
    const x = t * 400;
    const y = 40 + Math.sin(t * Math.PI * 2.5) * 25 + Math.cos(t * Math.PI * 4) * 8;
    const r = 1.5 + Math.sin(t * Math.PI * 6) * 0.8;
    points.push({ cx: x, cy: y, r });
  }

  return (
    <svg className={className} width="100%" height="80" viewBox="0 0 400 80" preserveAspectRatio="xMidYMid meet" fill="none" aria-hidden="true">
      {points.map((p, i) => (
        <circle key={i} cx={p.cx} cy={p.cy} r={p.r} fill="currentColor" opacity={0.2 + Math.sin(i * 0.3) * 0.1} />
      ))}
    </svg>
  );
};

export { DotWaterhole, DotRiverTrail, DotUShape, DotStarburst, DotJourneyPath };
